import React, { useState, useEffect, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { CenterFrameLayout } from 'Layouts';
import { addRedirectUrl } from 'State/Integrations/IntegrationsActions';
import { SHEER_PAGE_TYPES, SHEER_PAGE_STATUS, getSheerVerificationId } from 'Utils/SheerIdHelpers';
import { getMemberVerify } from 'Api/SheerIdService';
import { useSubscription } from 'Hooks/useSubscription';
import { SheerIdMilitaryVerificationPage } from './SheerIdMilitaryVerificationPage/SheerIdMilitaryVerificationPage';
import { SheerIdUploadPage } from './SheerIdUpload/SheerIdUploadPage';
import { SheerIdReviewPage } from './SheerIdReviewPage/SheerIdReviewPage';
import { SheerIdStatusPage } from './SheerIdStatusPage/SheerIdStatusPage';
import './SheerIdVerification.css';

const components = {
  [SHEER_PAGE_TYPES.MILITARY_FORM]: SheerIdMilitaryVerificationPage,
  [SHEER_PAGE_TYPES.DOC_UPLOAD]: SheerIdUploadPage,
  [SHEER_PAGE_TYPES.REVIEW]: SheerIdReviewPage,
  [SHEER_PAGE_TYPES.SUCCESS]: SheerIdStatusPage,
  [SHEER_PAGE_TYPES.ERROR]: SheerIdStatusPage,
  [SHEER_PAGE_TYPES.INELIGIBLE]: SheerIdStatusPage,
};

export const SheerIdVerificationPage = () => {
  const { bango, step } = useSelector((s) => s.integrations);
  const [pageProps, setPageProps] = useState({
    pageType: step || SHEER_PAGE_TYPES.MILITARY_FORM,
    params: {},
  });

  const [loading, setLoading] = useState(true);
  const [title, setTitle] = useState('');
  const location = useLocation();
  const { hasActiveSubscription, purchaseType } = useSubscription();
  const pageType = pageProps?.pageType;
  const stepParams = pageProps?.params;
  const { redirectUrl } = bango;
  const dispatch = useDispatch();
  const hasBackBangoButton =
    redirectUrl && (pageType === SHEER_PAGE_TYPES.MILITARY_FORM || pageType === SHEER_PAGE_TYPES.REVIEW);
  const hasBackToVerification = pageType === SHEER_PAGE_TYPES.DOC_UPLOAD || pageType === SHEER_PAGE_TYPES.ERROR;

  const setPage = useCallback(
    (newPageType, newStepParams) => {
      // Defaults when memberVerify is 404ing before form submission
      setPageProps({
        pageType: newPageType || SHEER_PAGE_TYPES.MILITARY_FORM,
        params: newStepParams || {},
      });
      window.scrollTo({ top: 0, behavior: 'smooth' });
    },
    [setPageProps]
  );

  useEffect(() => {
    const getVerificationStep = async () => {
      // Returns step, and other data associated with a user with a verification Id,
      setLoading(true);
      try {
        const verificationStep = await getMemberVerify();
        if (verificationStep?.bangoRedirectUrl) dispatch(addRedirectUrl(verificationStep.bangoRedirectUrl));
        setPage(SHEER_PAGE_STATUS[verificationStep?.currentStep], verificationStep);
        setLoading(false);
      } catch (error) {
        if (error?.currentStep) setPage(SHEER_PAGE_STATUS[error?.currentStep], error);
        setLoading(false);
      }
    };

    getVerificationStep();
  }, [setPage, dispatch]);

  useEffect(() => {
    const urlParams = new URLSearchParams(location.search);
    const sheerType = urlParams.get('sheerVerification') || pageProps.params.segment;
    const alreadyVerified = !!getSheerVerificationId();
    if (sheerType && alreadyVerified) localStorage.setItem('sheerIdType', sheerType);
    if (pageProps.params.verificationId) localStorage.setItem('sheerVerificationId', pageProps.params.verificationId);
  }, [pageProps.params, location.search]);

  useEffect(() => {
    //Existing Bango Users are ineligible
    if (hasActiveSubscription && purchaseType === 'bango') {
      setPage(SHEER_PAGE_TYPES.INELIGIBLE, stepParams);
    }
  }, [hasActiveSubscription, purchaseType, setPage, stepParams]);

  const layoutProps = {
    title,
    showLoader: loading,
    modernLayout: true,
    panelStyle: hasBackBangoButton || hasBackToVerification ? { marginTop: 20 } : {},
    customFrameClass: 'sheerIdFrameContainer',
  };

  const Component = components[pageType];
  return (
    <CenterFrameLayout {...layoutProps}>
      <Component setPage={setPage} pageType={pageType} setTitle={setTitle} {...stepParams} />
    </CenterFrameLayout>
  );
};
